import { FormStore, GlobalStore } from '@roc/feature-app-core';
import { parseAddress } from '@roc/feature-utils';
import { action, flow, makeObservable, observable } from 'mobx';
import { TitleHistoryLookupService } from '../services/titleHistoryLookupService';

const form = {
  fields: {
    address: {
      value: '',
      error: null,
      rule: '',
    },
    streetNumber: {
      value: '',
      error: null,
      rule: '',
    },
    streetName: {
      value: '',
      error: null,
      rule: '',
    },
    aptNumber: {
      value: '',
      error: null,
      rule: '',
    },
    city: {
      value: '',
      error: null,
      rule: '',
    },
    state: {
      value: '',
      error: null,
      rule: '',
    },
    zipCode: {
      value: '',
      error: null,
      rule: '',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class TitleHistoryLookupStore extends FormStore {
  globalStore: GlobalStore;
  private titleHistoryLookupService: TitleHistoryLookupService;
  rows: any[] = [];
  isLoading = false;
  hasSearched = false;

  constructor(globalStore: GlobalStore) {
    super(form, globalStore);
    this.globalStore = globalStore;
    this.titleHistoryLookupService = new TitleHistoryLookupService();

    makeObservable(this, {
      rows: observable,
      isLoading: observable,
      hasSearched: observable,
      handleAddressChange: action,
      lookupTitleHistory: flow,
      resetStore: action,
    });
  }

  handleAddressChange(value: string, addressDetails?, geometryDetails?) {
    if (!value) {
      ['address', 'streetNumber', 'streetName', 'aptNumber', 'city', 'state', 'zipCode']
        .forEach(field => this.onFieldChange(field, ''));
      return;
    }

    const parsedAddress = parseAddress(value, addressDetails, geometryDetails);
    const streetLine = [parsedAddress.street_number, parsedAddress.street]
      .filter(Boolean)
      .join(' ');

    this.onFieldChange('address', addressDetails && streetLine ? streetLine : value);
    if (parsedAddress.street) {
      this.setIfPresent('streetNumber', parsedAddress.street_number);
      this.onFieldChange('streetName', parsedAddress.street);
    } else {
      this.onFieldChange('streetNumber', '');
      this.onFieldChange('streetName', value);
    }
    this.setIfPresent('aptNumber', parsedAddress.aptNumber);
    this.setIfPresent('city', parsedAddress.city);
    this.setIfPresent('state', parsedAddress.state);
    this.setIfPresent('zipCode', parsedAddress.zip);
  }

  *lookupTitleHistory() {
    const values = this.getFormValues();

    const lookupRequest = {
      address: values.address,
      streetNumber: values.streetNumber,
      streetName: values.streetName,
      aptNumber: values.aptNumber,
      city: values.city,
      state: values.state,
      zipCode: values.zipCode,
    };
    const data = new FormData();
    data.append('form_information', JSON.stringify(lookupRequest));

    try {
      this.isLoading = true;
      const response = yield this.titleHistoryLookupService.lookupTitleHistory(data);
      this.rows = this.parseRows(response);
    } catch (e) {
      this.rows = [];
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There has been an error loading the title history',
      });
    } finally {
      this.isLoading = false;
      this.hasSearched = true;
    }
  }

  resetStore() {
    this.reset();
    this.rows = [];
    this.isLoading = false;
    this.hasSearched = false;
  }

  private parseRows(response): any[] {
    const payload = response?.data?.data;
    const grid = typeof payload === 'string' ? JSON.parse(payload) : payload;
    return grid?.rows ?? [];
  }

  private setIfPresent(field: string, value) {
    if (value !== null && value !== undefined && value !== '') {
      this.onFieldChange(field, value);
    }
  }
}
